import {
  type CSSProperties,
  type FC,
  type KeyboardEvent,
  forwardRef,
  type MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { observer } from 'mobx-react-lite'
import Tooltip, { tooltipClasses, type TooltipProps } from '@mui/material/Tooltip'
import { nanoid } from 'nanoid'
import { styled } from '@mui/material/styles'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import classNames from 'classnames'
import type { ForwardRefComponentProps } from 'shared/ui/Tooltip'
import { IconButton } from 'shared/ui/IconButton'
import { Button } from 'shared/ui/Button'
import { type IDropdownItem, type IDropdownProps } from 'shared/ui/Dropdown'
import { dropDownStore } from 'shared/ui/Dropdown/store'
import { DropdownList } from 'shared/ui/Dropdown/ui/DropdownList/DropdownList'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { getAriaLabel } from 'shared/lib'
import { ZIndex } from 'shared/constants/zIndex'
import styles from './styles.module.scss'

const ForwardRefComponent = forwardRef(function ForwardRefComponent(
  props: ForwardRefComponentProps,
  ref: React.Ref<HTMLDivElement>
) {
  return (
    <div {...props} ref={ref}>
      {props.children}
    </div>
  )
})

const HtmlTooltip = styled(
  ({
    className,
    ...props
  }: TooltipProps & {
    width?: CSSProperties['width']
    margin?: string | number
    margintop?: number | string
    marginleft?: number | string
    zindex?: number
  }) => {
    const popperModifiers = [...(props.PopperProps?.modifiers || [])]

    if (props.marginleft) {
      popperModifiers.push({
        name: 'offset',
        options: {
          offset: [props.marginleft, 0],
        },
      })
    }

    return (
      <Tooltip
        {...props}
        aria-label={getAriaLabel('HtmlTooltip')}
        classes={{ popper: className }}
        PopperProps={{
          ...props.PopperProps,
          modifiers: popperModifiers,
          style: { zIndex: props.zindex ?? ZIndex.OVERLAY_BASE_500 },
        }}
      />
    )
  }
)(({ theme, width, margin = 4, margintop }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    fontFamily: '"Inter", sans-serif',
    backgroundColor: 'transparent',
    color: 'transparent',
    width,
    maxWidth: width ? 'unset' : 335,
    padding: '0',
    borderRadius: '0',
    fontSize: theme.typography.pxToRem(13),
    border: 'none',
    margin: 0,
  },
  [`& div.${tooltipClasses.tooltip}.${tooltipClasses.tooltipPlacementBottom}`]: {
    marginTop: margin,
    marginBottom: margin,
  },
  ['&[data-popper-placement="top-start"] .top-dropdown-column-reverse-content']: {
    flexFlow: 'column-reverse',
  },
  ['&[data-popper-placement="top"] .top-dropdown-column-reverse-content']: {
    flexFlow: 'column-reverse',
  },
  ['&[data-popper-placement="top-end"] .top-dropdown-column-reverse-content']: {
    flexFlow: 'column-reverse',
  },
  [`& div.${tooltipClasses.tooltip}.${tooltipClasses.tooltipPlacementTop}`]: {
    marginBottom: margin,
    marginTop: margin,
  },
  [`& div.${tooltipClasses.tooltip}.${tooltipClasses.tooltipPlacementLeft}`]: {
    marginTop: margintop,
    marginRight: margin,
  },
  [`& div.${tooltipClasses.tooltip}.${tooltipClasses.tooltipPlacementRight}`]: {
    marginTop: margintop,
    marginLeft: margin,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: 'var(--background-neutral-tertiary)',
  },
  [`& .${tooltipClasses.arrow}:before`]: {
    borderRadius: '2px',
  },
}))

export const Dropdown: FC<IDropdownProps> = observer(
  ({
    items = [],
    onChange,
    onPressEnter,
    onToggle = () => {},
    icon = 'add',
    sizeTriggerButton = 'small',
    variantTriggerButton,
    typeButton = 'icon-button',
    placement,
    tooltipProps,
    buttonProps,
    iconButtonProps,
    customComponent,
    triggerComponent,
    width,
    childContainerWidth,
    maxHeight,
    margin,
    marginTop,
    marginLeft,
    withSearch,
    withTextInput,
    bottomActions,
    show = false,
    onClose,
    isHover = false,
    disabled = false,
    parent,
    parentsDropdownId,
    onChangeOpen = () => {},
    textFieldProps,
    className = '',
    onToggleHover,
    PopperProps = null,
    TransitionProps,
    disableStopPropagation = false,
    topListComponent,
    underControlComponent,
    overlayModalsIds,
    autoOpen,
    ariaLabel,
    zIndex = ZIndex.OVERLAY_BASE_500,
    propsDropdownList,
    withToggleTriggerOpen,
    placeholder,
    checkIsCanOpen,
    tabIndex,
  }) => {
    const [open, setOpen] = useState(false)
    const [nanoId] = useState(() => nanoid())
    const triggerRef = useRef<HTMLDivElement>(null)
    const isFocusable = tabIndex !== undefined
    ariaLabel = `Dropdown_${ariaLabel}`

    const onOpen = (value: boolean) => {
      setOpen(value)
      dropDownStore.setOpen(nanoId, value)
    }

    const handleTooltipClose = (restoreFocus = true) => {
      if (overlayModalsIds) {
        let hasOverlayModal = false

        overlayModalsIds.map((modalId) => {
          if (modalStore.hasModal(modalId)) {
            hasOverlayModal = true
            return
          }
        })

        if (hasOverlayModal) return
      }

      if (open) {
        onOpen(false)
        onToggle(false)
        onClose?.()

        if (restoreFocus && isFocusable) {
          requestAnimationFrame(() => {
            triggerRef.current?.focus()
          })
        }
      }
    }

    const handleTooltipOpen = async (e?: MouseEvent<HTMLDivElement>) => {
      !disableStopPropagation && e?.stopPropagation()

      if (!open && checkIsCanOpen) {
        try {
          const isCanOpen = await checkIsCanOpen?.()
          if (!isCanOpen) return
        } catch (e) {
          console.error(e)
        }
      }

      if (disabled) return

      if (withToggleTriggerOpen) {
        onOpen(!open)
        onToggle(!open)
      } else {
        onOpen(true)
        onToggle(true)
      }

      dropDownStore.allHide(nanoId, parentsDropdownId)
    }

    const handleChange = (item: IDropdownItem, parent?: IDropdownItem) => {
      onChange?.(item, parent)

      if (item.disabledClosedTooltip) return
      handleTooltipClose()
    }

    useEffect(() => {
      dropDownStore.setItem(nanoId, { setOpen: setOpen, open: open })
      autoOpen && onOpen(true)

      return () => {
        dropDownStore.deleteItem(nanoId)
      }
    }, [])

    useEffect(() => {
      if (show) {
        handleTooltipOpen()
      } else {
        handleTooltipClose()
      }
    }, [show])

    useEffect(() => {
      onChangeOpen(open)
    }, [open])

    const component = customComponent ? (
      customComponent(handleTooltipClose, nanoId)
    ) : (
      <DropdownList
        dropdownId={nanoId}
        width={width}
        childContainerWidth={childContainerWidth}
        items={items}
        maxHeight={maxHeight}
        onChange={handleChange}
        onPressEnter={onPressEnter}
        parent={parent}
        withSearch={withSearch}
        bottomActions={bottomActions?.(handleTooltipClose, nanoId)}
        withTextInput={withTextInput}
        textFieldProps={textFieldProps}
        topListComponent={topListComponent?.(handleTooltipClose, nanoId)}
        underControlComponent={underControlComponent?.(handleTooltipClose, nanoId)}
        placeholder={placeholder}
        {...propsDropdownList}
        onClose={handleTooltipClose}
      />
    )

    const renderButton = () => {
      if (triggerComponent) {
        return triggerComponent(open)
      }
      if (typeButton === 'icon-button') {
        return (
          <IconButton
            tag='span'
            icon={icon}
            size={sizeTriggerButton}
            color={'secondary'}
            active={open}
            variant={variantTriggerButton}
            {...iconButtonProps}
            tooltipProps={
              tooltipProps ? { ...tooltipProps, label: open ? '' : tooltipProps.label } : undefined
            }
            disabled={disabled}
            ariaLabel={getAriaLabel('Trigger')}
          />
        )
      }

      return <Button icon={icon} disabled={disabled} {...buttonProps} />
    }

    if (isHover) {
      return (
        <div className={styles.wrap} aria-label={getAriaLabel(ariaLabel)}>
          <HtmlTooltip
            open={open}
            PopperProps={{
              ...PopperProps,
            }}
            TransitionProps={{
              ...TransitionProps,
            }}
            placement={placement}
            arrow={false}
            title={component}
            width={width}
            margin={margin}
            margintop={marginTop}
            marginleft={marginLeft}
            zindex={zIndex}
            onOpen={() => {
              onOpen(true)
            }}
            onClose={() => {
              onOpen(false)
            }}
            aria-label={getAriaLabel(ariaLabel, 'Content')}
          >
            <ForwardRefComponent>
              <div onClick={handleTooltipOpen}>{renderButton()}</div>
            </ForwardRefComponent>
          </HtmlTooltip>
        </div>
      )
    }

    const onMouseLeave = () => {
      onToggleHover?.(false)
    }

    const onMouseEnter = () => {
      onToggleHover?.(true)
    }

    const handleTriggerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        handleTooltipOpen()
      }
    }

    return (
      <div
        className={classNames(styles.wrap, className, {
          [styles.fullDisabled]: disabled,
        })}
        aria-label={getAriaLabel(ariaLabel)}
      >
        <ClickAwayListener onClickAway={() => handleTooltipClose(false)}>
          <div aria-label={getAriaLabel(ariaLabel, 'Wrap')}>
            <HtmlTooltip
              placement={placement}
              arrow={false}
              open={open}
              PopperProps={{
                disablePortal: false,
                ...PopperProps,
              }}
              TransitionProps={{
                ...TransitionProps,
              }}
              onClose={() => handleTooltipClose(false)}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={component}
              width={width}
              margin={margin}
              margintop={marginTop}
              marginleft={marginLeft}
              zindex={zIndex}
              aria-label={getAriaLabel(ariaLabel, 'Content')}
            >
              <ForwardRefComponent>
                <div
                  ref={triggerRef}
                  tabIndex={tabIndex}
                  className={isFocusable ? styles.triggerFocusable : undefined}
                  onKeyDown={isFocusable ? handleTriggerKeyDown : undefined}
                  role={isFocusable ? 'button' : undefined}
                  onClick={handleTooltipOpen}
                  onMouseLeave={onMouseLeave}
                  onMouseEnter={onMouseEnter}
                  aria-label={getAriaLabel(ariaLabel, 'Trigger')}
                >
                  {renderButton()}
                </div>
              </ForwardRefComponent>
            </HtmlTooltip>
          </div>
        </ClickAwayListener>
      </div>
    )
  }
)
